<script setup lang="ts">
import { computed } from 'vue'
import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/vue/24/solid'
import { useI18n } from 'vue-i18n'
import { useToastStore, type ToastType } from '@/shared/stores/toast.store'

const { t } = useI18n()
const store = useToastStore()
const items = computed(() => store.items)

const variants: Record<
  ToastType,
  {
    icon: typeof CheckCircleIcon
    titleKey: string
    shell: string
    glow: string
    accent: string
    progress: string
    iconWrap: string
    iconColor: string
    eyebrow: string
    headline: string
    detail: string
    close: string
  }
> = {
  success: {
    icon: CheckCircleIcon,
    titleKey: 'common.toastSuccess',
    shell: 'border-emerald-200/70 bg-white/85 dark:border-emerald-500/25 dark:bg-emerald-950/75',
    glow: 'from-emerald-400/25 via-emerald-300/5 to-transparent',
    accent: 'bg-gradient-to-b from-emerald-400 to-emerald-600',
    progress: 'bg-emerald-500',
    iconWrap: 'bg-emerald-500/12 ring-1 ring-emerald-500/15 dark:bg-emerald-400/12 dark:ring-emerald-400/20',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    eyebrow: 'text-emerald-700/80 dark:text-emerald-300/80',
    headline: 'text-slate-900 dark:text-emerald-50',
    detail: 'text-slate-600 dark:text-emerald-100/70',
    close: 'text-slate-400 hover:bg-emerald-500/10 hover:text-emerald-800 dark:text-emerald-200/60 dark:hover:bg-emerald-400/10 dark:hover:text-emerald-50',
  },
  error: {
    icon: ExclamationCircleIcon,
    titleKey: 'common.toastError',
    shell: 'border-rose-200/70 bg-white/85 dark:border-rose-500/25 dark:bg-rose-950/75',
    glow: 'from-rose-400/25 via-rose-300/5 to-transparent',
    accent: 'bg-gradient-to-b from-rose-400 to-rose-600',
    progress: 'bg-rose-500',
    iconWrap: 'bg-rose-500/12 ring-1 ring-rose-500/15 dark:bg-rose-400/12 dark:ring-rose-400/20',
    iconColor: 'text-rose-600 dark:text-rose-400',
    eyebrow: 'text-rose-700/80 dark:text-rose-300/80',
    headline: 'text-slate-900 dark:text-rose-50',
    detail: 'text-slate-600 dark:text-rose-100/70',
    close: 'text-slate-400 hover:bg-rose-500/10 hover:text-rose-800 dark:text-rose-200/60 dark:hover:bg-rose-400/10 dark:hover:text-rose-50',
  },
  warning: {
    icon: ExclamationTriangleIcon,
    titleKey: 'common.toastWarning',
    shell: 'border-amber-200/70 bg-white/85 dark:border-amber-500/25 dark:bg-amber-950/75',
    glow: 'from-amber-400/25 via-amber-300/5 to-transparent',
    accent: 'bg-gradient-to-b from-amber-400 to-amber-600',
    progress: 'bg-amber-500',
    iconWrap: 'bg-amber-500/12 ring-1 ring-amber-500/15 dark:bg-amber-400/12 dark:ring-amber-400/20',
    iconColor: 'text-amber-600 dark:text-amber-400',
    eyebrow: 'text-amber-800/80 dark:text-amber-300/80',
    headline: 'text-slate-900 dark:text-amber-50',
    detail: 'text-slate-600 dark:text-amber-100/70',
    close: 'text-slate-400 hover:bg-amber-500/10 hover:text-amber-900 dark:text-amber-200/60 dark:hover:bg-amber-400/10 dark:hover:text-amber-50',
  },
  info: {
    icon: InformationCircleIcon,
    titleKey: 'common.toastInfo',
    shell: 'border-sky-200/70 bg-white/85 dark:border-sky-500/25 dark:bg-sky-950/75',
    glow: 'from-sky-400/25 via-sky-300/5 to-transparent',
    accent: 'bg-gradient-to-b from-sky-400 to-sky-600',
    progress: 'bg-sky-500',
    iconWrap: 'bg-sky-500/12 ring-1 ring-sky-500/15 dark:bg-sky-400/12 dark:ring-sky-400/20',
    iconColor: 'text-sky-600 dark:text-sky-400',
    eyebrow: 'text-sky-700/80 dark:text-sky-300/80',
    headline: 'text-slate-900 dark:text-sky-50',
    detail: 'text-slate-600 dark:text-sky-100/70',
    close: 'text-slate-400 hover:bg-sky-500/10 hover:text-sky-800 dark:text-sky-200/60 dark:hover:bg-sky-400/10 dark:hover:text-sky-50',
  },
}

function splitMessage(message: string): { headline: string; detail: string } {
  const normalized = message.replace(/\.\.\./g, '…').trim()
  const match = normalized.match(/^(.+?[….!?。！？])\s+(.+)$/u)
  if (match?.[1] && match[2]) {
    return { headline: match[1].trim(), detail: match[2].trim() }
  }
  return { headline: normalized, detail: '' }
}

function isExportMessage(message: string): boolean {
  return /xuất|export|导出/i.test(message)
}

function iconFor(item: { type: ToastType; message: string }) {
  if (item.type === 'info' && isExportMessage(item.message)) return ArrowDownTrayIcon
  return variants[item.type].icon
}

const cards = computed(() =>
  items.value.map((item) => {
    const parts = splitMessage(item.message)
    return {
      ...item,
      headline: parts.headline,
      detail: parts.detail,
      exportish: item.type === 'info' && isExportMessage(item.message),
      icon: iconFor(item),
      variant: variants[item.type],
    }
  }),
)
</script>

<template>
  <Teleport to="body">
    <div
      class="hr-toast-host pointer-events-none fixed inset-x-0 z-[9999] flex flex-col items-center gap-2.5 px-3 sm:inset-x-auto sm:right-4 sm:items-stretch sm:px-0"
      aria-live="polite"
      aria-relevant="additions removals"
    >
      <TransitionGroup
        tag="div"
        class="flex w-full max-w-[24rem] flex-col gap-2.5 sm:w-[24rem]"
        enter-active-class="hr-toast-enter-active"
        leave-active-class="hr-toast-leave-active"
        enter-from-class="hr-toast-enter-from"
        leave-to-class="hr-toast-leave-to"
        move-class="hr-toast-move"
      >
        <div
          v-for="item in cards"
          :key="item.id"
          class="hr-toast-card group pointer-events-auto relative overflow-hidden rounded-[1.15rem] border shadow-[0_18px_50px_-18px_rgba(15,23,42,0.35)] backdrop-blur-xl dark:shadow-[0_22px_56px_-16px_rgba(0,0,0,0.72)]"
          :class="item.variant.shell"
          role="status"
          @mouseenter="store.pause(item.id)"
          @mouseleave="store.resume(item.id)"
        >
          <span
            class="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b"
            :class="item.variant.glow"
            aria-hidden="true"
          />
          <span
            class="absolute inset-y-0 left-0 w-[3px]"
            :class="item.variant.accent"
            aria-hidden="true"
          />

          <div class="relative flex items-start gap-3 px-3.5 py-3.5 pl-4 sm:gap-3.5 sm:px-4">
            <div
              class="relative mt-0.5 grid h-11 w-11 shrink-0 place-items-center rounded-2xl"
              :class="item.variant.iconWrap"
            >
              <span
                v-if="item.exportish"
                class="hr-toast-pulse absolute inset-0 rounded-2xl"
                :class="item.variant.iconWrap"
                aria-hidden="true"
              />
              <component
                :is="item.icon"
                class="relative h-5 w-5"
                :class="item.variant.iconColor"
                aria-hidden="true"
              />
            </div>

            <div class="min-w-0 flex-1 pt-0.5">
              <p
                class="text-[0.68rem] font-semibold uppercase tracking-[0.08em]"
                :class="item.variant.eyebrow"
              >
                {{ t(item.variant.titleKey) }}
              </p>
              <p
                class="mt-1 text-[0.9375rem] font-semibold leading-snug tracking-[-0.01em]"
                :class="item.variant.headline"
              >
                {{ item.headline }}
              </p>
              <p
                v-if="item.detail"
                class="mt-1 text-[0.8125rem] font-medium leading-relaxed"
                :class="item.variant.detail"
              >
                {{ item.detail }}
              </p>
            </div>

            <button
              type="button"
              class="grid h-8 w-8 shrink-0 place-items-center rounded-xl transition"
              :class="item.variant.close"
              :aria-label="t('common.close')"
              @click="store.dismiss(item.id)"
            >
              <XMarkIcon class="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          <div class="hr-toast-progress-track absolute inset-x-0 bottom-0 h-[2px] bg-slate-900/5 dark:bg-white/10">
            <span
              class="hr-toast-progress-bar block h-full origin-left"
              :class="item.variant.progress"
              :style="{ animationDuration: `${item.duration}ms` }"
            />
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
.hr-toast-host {
  top: max(0.75rem, env(safe-area-inset-top, 0px));
}

.hr-toast-enter-active,
.hr-toast-leave-active,
.hr-toast-move {
  transition:
    opacity 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.28s cubic-bezier(0.22, 1, 0.36, 1);
}

.hr-toast-enter-from,
.hr-toast-leave-to {
  opacity: 0;
  transform: translateY(-0.75rem) scale(0.96);
}

.hr-toast-progress-bar {
  animation-name: hr-toast-progress;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}

.hr-toast-card:hover .hr-toast-progress-bar,
.hr-toast-card:hover .hr-toast-pulse {
  animation-play-state: paused;
}

.hr-toast-pulse {
  animation: hr-toast-pulse 1.6s ease-out infinite;
}

@keyframes hr-toast-progress {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}

@keyframes hr-toast-pulse {
  0% {
    transform: scale(1);
    opacity: 0.9;
  }
  70% {
    transform: scale(1.35);
    opacity: 0;
  }
  100% {
    transform: scale(1.35);
    opacity: 0;
  }
}

@media (min-width: 640px) {
  .hr-toast-host {
    top: 4.25rem;
  }

  .hr-toast-enter-from,
  .hr-toast-leave-to {
    transform: translateX(1.1rem) scale(0.96);
  }
}

@media (prefers-reduced-motion: reduce) {
  .hr-toast-enter-active,
  .hr-toast-leave-active,
  .hr-toast-move,
  .hr-toast-progress-bar,
  .hr-toast-pulse {
    animation: none !important;
    transition: none !important;
  }
}
</style>
